import React, { useState } from 'react';
import { getPayDayStr, isPayDayPassed, fmtPayDay } from '../utils/payPeriod';

function formatTime(t) {
  if (t == null) return '';
  const s = String(t).padStart(4, '0');
  return `${s.slice(0, 2)}:${s.slice(2)}`;
}

function formatDate(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${parseInt(m)}/${parseInt(day)}/${y}`;
}

function formatDateShort(d) {
  if (!d) return '';
  const [, m, day] = d.split('-');
  return `${parseInt(m)}/${parseInt(day)}`;
}

function formatBookedOn(dt) {
  if (!dt) return '';
  const date = dt.split('T')[0];
  return formatDate(date);
}

const SHORT_TAGS = { BREAKFAST: 'BF', LUNCH: 'Lun', DINNER: 'Din', COFFEE_BREAK: 'CB', CASH_CONCESSION: 'CC', RECEPTION: 'Rec' };
function shortTag(tag) {
  return SHORT_TAGS[tag] || tag;
}

export default function ShiftTable({ shifts, billingType, clientView, onNoteSave, onTagAssign, onMarkPaid, onClientMarkPaid, onCancel, onNoCharge, adminView, commissionRate }) {
  const [editNotes, setEditNotes] = useState({});
  const [saving, setSaving] = useState({});
  const [tagAssign, setTagAssign] = useState({});
  const [editingTag, setEditingTag] = useState({});
  const [sortCol, setSortCol] = useState('shdate');
  const [sortDir, setSortDir] = useState('asc');
  // The two notes columns are the widest thing in the table and are usually empty,
  // so the client view collapses them behind a toggle. Admin keeps them always on.
  const [showNotes, setShowNotes] = useState(false);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function SortTh({ col, children }) {
    const active = sortCol === col;
    return (
      <th onClick={() => toggleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
        {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ color: '#ccc' }}>⇅</span>}
      </th>
    );
  }

  const TAGS = ['BREAKFAST', 'LUNCH', 'DINNER', 'RECEPTION', 'COFFEE_BREAK', 'CASH_CONCESSION', '3:B-L-D', '2:B-L', '5:L-D'];

  async function saveNote(shiftId) {
    setSaving(p => ({ ...p, [shiftId]: true }));
    await onNoteSave(shiftId, editNotes[shiftId]);
    setSaving(p => ({ ...p, [shiftId]: false }));
    setEditNotes(p => { const n = { ...p }; delete n[shiftId]; return n; });
  }

  async function assignTag(shiftId, billId) {
    const rawTag = tagAssign[shiftId];
    if (!rawTag) return;
    await onTagAssign(billId, shiftId, rawTag);
    setTagAssign(p => { const n = { ...p }; delete n[shiftId]; return n; });
    setEditingTag(p => { const n = { ...p }; delete n[shiftId]; return n; });
  }

  function cancelTagEdit(shiftId) {
    setTagAssign(p => { const n = { ...p }; delete n[shiftId]; return n; });
    setEditingTag(p => { const n = { ...p }; delete n[shiftId]; return n; });
  }

  if (!shifts || shifts.length === 0) return <p style={{ color: '#aaa', fontSize: 13 }}>No shifts to display.</p>;

  const sorted = [...shifts].sort((a, b) => {
    let va, vb;
    if (sortCol === 'shdate') { va = a.shdate || ''; vb = b.shdate || ''; }
    else if (sortCol === 'bookedOn') { va = a.SelfSchAssignTime || ''; vb = b.SelfSchAssignTime || ''; }
    else if (sortCol === 'rate') { va = a.rate || 0; vb = b.rate || 0; }
    else if (sortCol === 'property') { va = a.property || ''; vb = b.property || ''; }
    else { va = a.shdate || ''; vb = b.shdate || ''; }
    const cmp = typeof va === 'number' ? va - vb : va.localeCompare(vb);
    const primary = sortDir === 'asc' ? cmp : -cmp;
    if (primary !== 0) return primary;
    return (a.tmstart || 0) - (b.tmstart || 0);
  });

  // Hidden only for clients, and only while collapsed.
  const notesVisible = adminView || !clientView || showNotes;
  const notesCount = shifts.filter(s => s.adminNotes || s.clientNotes).length;

  // Cost is dropped from every client view to save width — per-shift clients still
  // see Owed, the filter bar totals the selection, and commission viewers know the
  // agreed rate, so the Commission figure stands on its own.
  const costVisible = adminView || !clientView;

  return (
    <>
      {!adminView && clientView && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            type="button"
            className="toggle-btn"
            onClick={() => setShowNotes(v => !v)}
            style={{ fontSize: 12 }}
          >
            {showNotes ? 'Hide notes' : `Show notes${notesCount ? ` (${notesCount})` : ''}`}
          </button>
        </div>
      )}
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <SortTh col="bookedOn"><span className="th-break">Booked</span> On</SortTh>
            <SortTh col="property">Property</SortTh>
            <th className="col-userid">User ID</th>
            <SortTh col="shdate"><span className="th-break">Shift</span> Date</SortTh>
            <th className="col-time">Start–End</th>
            <th>Tag</th>
            {billingType === 'perMonth' && <th style={{ whiteSpace: 'nowrap' }}>Payday</th>}
            {costVisible && <SortTh col="rate">Cost</SortTh>}
            {commissionRate && billingType === 'perShift' && <th style={{ color: '#38a169' }}>Commission</th>}
            {billingType === 'perShift' && <th>Owed</th>}
            {billingType === 'perShift' && <th>Status</th>}
            {billingType === 'perShift' && <th style={{ whiteSpace: 'nowrap' }}>Due On</th>}
            {billingType === 'perShift' && (adminView || clientView) && <th></th>}
            {billingType === 'perShift' && <th>Date Paid</th>}
            {notesVisible && <th>Admin Notes</th>}
            {notesVisible && <th>Client Notes</th>}
            {adminView && <th>Assigned Time</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => {
            const shiftKey = String(s.shiftId || i);
            const isUnid = !s.canceled && !s.noCharge && (s.needsIdentification || s.normalizedTag === 'UNIDENTIFIED');
            const statusClass = s.canceled ? 'badge-unpaid' : s.noCharge ? 'badge-nocharge' : isUnid ? 'badge-unid' : s.paid ? 'badge-paid' : s.clientMarkedPaid ? 'badge-partial' : 'badge-unpaid';
            const statusLabel = s.canceled ? 'canceled' : s.noCharge ? 'nocharge' : isUnid ? 'UNIDENTIFIED ⚠' : s.paid ? 'paid' : s.clientMarkedPaid ? 'pending ⏳' : 'unpaid';
            const owed = (s.paid || s.canceled || s.noCharge) ? 0 : (s.rate || 0);

            const rowStyle = s.canceled
              ? { background: '#f9f9f9', color: '#aaa' }
              : s.noCharge ? { background: '#f7f8fa' }
              : isUnid ? { background: '#fffbeb' } : {};

            return (
              <tr key={shiftKey} style={rowStyle}>
                <td style={{ whiteSpace: 'nowrap', textDecoration: s.canceled ? 'line-through' : 'none' }}>
                  <span className="date-long">{formatBookedOn(s.SelfSchAssignTime)}</span>
                  <span className="date-short">{formatDateShort(s.SelfSchAssignTime?.split('T')[0])}</span>
                </td>
                <td className="cell-trunc" style={{ textDecoration: s.canceled ? 'line-through' : 'none' }}>{s.property}</td>
                <td className="col-userid" style={{ textDecoration: s.canceled ? 'line-through' : 'none' }}>{s.user}</td>
                <td style={{ whiteSpace: 'nowrap', textDecoration: s.canceled ? 'line-through' : 'none' }}>
                  <span className="date-long">{formatDate(s.shdate)}</span>
                  <span className="date-short">{formatDateShort(s.shdate)}</span>
                </td>
                <td className="col-time" style={{ whiteSpace: 'nowrap', textDecoration: s.canceled ? 'line-through' : 'none' }}>
                  {s.tmstartFormatted || formatTime(s.tmstart)}{(s.tmendFormatted || formatTime(s.tmend)) ? `–${s.tmendFormatted || formatTime(s.tmend)}` : ''}
                </td>
                <td className={adminView && (isUnid || editingTag[shiftKey]) ? '' : 'cell-trunc'}>
                  {adminView && (isUnid || editingTag[shiftKey]) ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <select
                        value={tagAssign[shiftKey] ?? s.normalizedTag ?? ''}
                        onChange={e => setTagAssign(p => ({ ...p, [shiftKey]: e.target.value }))}
                        style={{ width: 140 }}
                        autoFocus={!isUnid}
                      >
                        <option value="">Select tag...</option>
                        {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button className="btn btn-primary btn-sm" onClick={() => assignTag(shiftKey, s.billId)}>Set</button>
                      {!isUnid && (
                        <button className="btn btn-secondary btn-sm" onClick={() => cancelTagEdit(shiftKey)}>✕</button>
                      )}
                    </div>
                  ) : adminView ? (
                    <span
                      className="badge badge-tag-editable"
                      title="Click to change tag"
                      onClick={() => setEditingTag(p => ({ ...p, [shiftKey]: true }))}
                    >
                      <span className="tag-full">{s.normalizedTag}</span>
                      <span className="tag-short">{shortTag(s.normalizedTag)}</span>
                    </span>
                  ) : (
                    <span className={isUnid ? 'badge badge-unid' : ''}>
                      <span className="tag-full">{s.normalizedTag}</span>
                      <span className="tag-short">{shortTag(s.normalizedTag)}</span>
                    </span>
                  )}
                </td>
                {billingType === 'perMonth' && (
                  <td style={{ whiteSpace: 'nowrap', color: '#718096' }}>
                    {s.shdate ? fmtPayDay(s.shdate) : '—'}
                  </td>
                )}
                {costVisible && (billingType === 'perShift'
                  ? <td>${(s.rate || 0).toFixed(2)}</td>
                  : <td style={{ color: '#718096', fontStyle: 'italic' }}>Monthly</td>)}
                {commissionRate && billingType === 'perShift' && (
                  <td style={{ color: s.canceled ? '#ccc' : '#38a169', fontWeight: 500 }}>
                    {s.canceled ? '—' : `$${(((s.rate || 0) * commissionRate)).toFixed(2)}`}
                  </td>
                )}
                {billingType === 'perShift' && <td>${owed.toFixed(2)}</td>}
                {billingType === 'perShift' && <td><span className={`badge ${statusClass}`}>{statusLabel}</span></td>}
                {billingType === 'perShift' && (() => {
                  if (s.canceled || !s.shdate) return <td style={{ color: '#ccc' }}>—</td>;
                  if (s.paid || s.noCharge) {
                    return (
                      <td style={{ whiteSpace: 'nowrap', color: '#38a169', fontWeight: 500 }}>
                        {fmtPayDay(s.shdate)}
                      </td>
                    );
                  }
                  const overdue = isPayDayPassed(s.shdate);
                  return (
                    <td style={{ whiteSpace: 'nowrap', fontWeight: overdue ? 600 : 400, color: overdue ? '#e53e3e' : '#718096' }}>
                      {fmtPayDay(s.shdate)}
                      {overdue && <span style={{ marginLeft: 3, fontSize: 10 }}>●</span>}
                    </td>
                  );
                })()}
                {billingType === 'perShift' && (adminView || clientView) && (
                  // 170px reserves room for the admin's three buttons. A client has at
                  // most one ("I Paid This") and often none, so holding that width there
                  // opens a dead gap between Due On and Date Paid.
                  <td style={{ whiteSpace: 'nowrap', minWidth: adminView ? 170 : 0 }}>
                    {adminView && !isUnid && !s.canceled && !s.noCharge && onMarkPaid && (
                      <button
                        className={`btn btn-sm ${s.paid ? 'btn-secondary' : 'btn-primary'}`}
                        onClick={() => onMarkPaid(s.billId, shiftKey, !s.paid)}
                      >
                        {s.paid ? 'Unmark' : 'Paid'}
                      </button>
                    )}
                    {adminView && !s.canceled && !s.paid && onNoCharge && (
                      <button
                        className={`btn btn-sm ${s.noCharge ? 'btn-secondary' : 'btn-warning'}`}
                        style={{ marginLeft: 4 }}
                        title={s.noCharge ? undefined : 'No Charge'}
                        onClick={() => onNoCharge(s.billId, shiftKey, !s.noCharge)}
                      >
                        {s.noCharge ? 'Charge' : 'NC'}
                      </button>
                    )}
                    {adminView && onCancel && (
                      <button
                        className={`btn btn-sm ${s.canceled ? 'btn-primary' : 'btn-danger'}`}
                        style={{ marginLeft: 4, textDecoration: 'none', opacity: 1 }}
                        onClick={() => onCancel(s.billId, shiftKey, !s.canceled)}
                      >
                        {s.canceled ? 'Reactivate' : 'Cancel'}
                      </button>
                    )}
                    {clientView && !s.paid && !s.canceled && !isUnid && onClientMarkPaid && (
                      <button
                        className={`btn btn-sm ${s.clientMarkedPaid ? 'btn-secondary' : 'btn-primary'}`}
                        onClick={() => onClientMarkPaid(shiftKey, !s.clientMarkedPaid)}
                      >
                        {s.clientMarkedPaid ? 'Undo' : 'I Paid This'}
                      </button>
                    )}
                  </td>
                )}
                {billingType === 'perShift' && <td style={{ whiteSpace: 'nowrap' }}>{formatDate(s.datePaid)}</td>}
                {notesVisible && <td>
                  {adminView ? (
                    `admin_${shiftKey}` in editNotes ? (
                      <div style={{ display: 'flex', gap: 3 }}>
                        <input
                          value={editNotes[`admin_${shiftKey}`]}
                          onChange={e => setEditNotes(p => ({ ...p, [`admin_${shiftKey}`]: e.target.value }))}
                          style={{ width: 110, padding: '1px 4px', fontSize: 11 }}
                          autoFocus
                        />
                        <button className="btn btn-primary btn-sm" onClick={() => { onNoteSave && onNoteSave(shiftKey, editNotes[`admin_${shiftKey}`], 'admin'); setEditNotes(p => { const n={...p}; delete n[`admin_${shiftKey}`]; return n; }); }}>✓</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditNotes(p => { const n={...p}; delete n[`admin_${shiftKey}`]; return n; })}>✕</button>
                      </div>
                    ) : (
                      <span style={{ color: s.adminNotes ? '#1a1a2e' : '#ccc', cursor: 'pointer' }} onClick={() => setEditNotes(p => ({ ...p, [`admin_${shiftKey}`]: s.adminNotes || '' }))}>
                        {s.adminNotes || '+ note'}
                      </span>
                    )
                  ) : (
                    <span style={{ color: '#718096' }}>{s.adminNotes || '—'}</span>
                  )}
                </td>}
                {notesVisible && <td style={{ minWidth: 160 }}>
                  {clientView ? (
                    shiftKey in editNotes ? (
                      <div style={{ display: 'flex', gap: 3 }}>
                        <input
                          value={editNotes[shiftKey]}
                          onChange={e => setEditNotes(p => ({ ...p, [shiftKey]: e.target.value }))}
                          style={{ width: 110, padding: '1px 4px', fontSize: 11 }}
                          autoFocus
                        />
                        <button className="btn btn-primary btn-sm" disabled={saving[shiftKey]} onClick={() => saveNote(shiftKey)}>✓</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditNotes(p => { const n={...p}; delete n[shiftKey]; return n; })}>✕</button>
                      </div>
                    ) : (
                      <span style={{ color: s.clientNotes ? '#1a1a2e' : '#ccc', cursor: 'pointer' }} onClick={() => setEditNotes(p => ({ ...p, [shiftKey]: s.clientNotes || '' }))}>
                        {s.clientNotes || '+ note'}
                      </span>
                    )
                  ) : (
                    <span style={{ color: '#718096' }}>{s.clientNotes || '—'}</span>
                  )}
                </td>}
                {adminView && <td style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap' }}>{s.SelfSchAssignTime || '—'}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}
